import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/gtmTracking';

/**
 * Google Tag Manager Page View Tracker
 *
 * Tracks page views for client-side navigation in React Router.
 * Pushes page view events to GTM dataLayer when the route changes.
 *
 * This is necessary because React Router uses client-side navigation,
 * which doesn't trigger full page reloads, so GTM won't automatically
 * track page views on route changes.
 */
export function GTMPageViewTracker() {
  const location = useLocation();
  const previousPathname = useRef<string | null>(null);

  useEffect(() => {
    const pathname = location.pathname + location.search;

    // Skip tracking on initial mount (GTM handles initial page view)
    if (previousPathname.current === null) {
      previousPathname.current = pathname;
      return undefined;
    }

    if (previousPathname.current === pathname) {
      return undefined;
    }

    previousPathname.current = pathname;

    const doTrack = (): boolean => {
      if (typeof window === 'undefined' || !window.dataLayer) {
        return false;
      }
      trackPageView(pathname);
      return true;
    };

    if (doTrack()) return undefined;

    const maxRetries = 10;
    let retries = 0;
    const retryInterval = setInterval(() => {
      retries++;
      if (doTrack() || retries >= maxRetries) {
        clearInterval(retryInterval);
      }
    }, 100);

    return () => clearInterval(retryInterval);
  }, [location.pathname, location.search]);

  return null;
}
